import { Button, Skeleton, Tag, Tooltip } from 'antd'
import { ColumnType, SortOrder } from 'antd/lib/table/interface'
import * as React from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import { calculateRate } from '../../types/Fees'
import Amount from '../Amount'
import ButtonGroup from '../ButtonGroup'
import { BillSort, editBillAction, viewBillAction } from './types'
import {
  EditFilled,
  SearchOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import ContributorButton from '../ContributorButton'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import { useFinancingRequest, useMode, useSmartHubInvoice } from './queries'
import { TranslatedMessage } from '@library/react-toolkit'
import { InvoiceWithTotal } from '../../types'
import { useOrganizationByName } from '../../queries'

const financingStatusIcons = {
  financed: <DollarOutlined style={{ color: '#51B148', fontSize: 20 }} />,
  published: <MinusCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />,
  pending: <ClockCircleOutlined style={{ color: '#FBBC05', fontSize: 20 }} />,
}

const FinancingStatus = ({
  organization,
  id,
}: {
  organization: string
  id: number
}) => {
  const financingRequestQuery = useFinancingRequest(organization, id)
  const invoiceQuery = useSmartHubInvoice(organization, id)
  const invoiceStatus =
    invoiceQuery.data === 'Not Found' ? undefined : invoiceQuery.data?.status
  const requestStatus =
    financingRequestQuery.data === 'Not Found'
      ? undefined
      : financingRequestQuery.data?.status
  const isLoading = invoiceQuery.isLoading || financingRequestQuery.isLoading
  const isFinanced = requestStatus === 'Financed'
  const isPending =
    requestStatus === 'Pending Offers' || requestStatus === 'Posted Request'
  const icon = isFinanced
    ? financingStatusIcons.financed
    : isPending
    ? financingStatusIcons.pending
    : financingStatusIcons.published

  return (
    <Skeleton
      avatar={{ size: 'small' }}
      round
      title={false}
      paragraph={false}
      active={isLoading}
      loading={isLoading}
    >
      {!invoiceStatus ? null : (
        <Tooltip
          placement="right"
          title={
            <TranslatedMessage
              id={isFinanced ? 'financed' : isPending ? 'pending' : 'published'}
            />
          }
        >
          {icon}
        </Tooltip>
      )}
    </Skeleton>
  )
}

const DiscountCell = ({ discount, amount, unitID }: InvoiceWithTotal) =>
  discount === undefined ? (
    <FormattedMessage id="notApplicable" />
  ) : (
    <Amount amount={-calculateRate(discount.rate, amount)} unit={unitID} />
  )

const TaxCell = ({ tax, discount, amount, unitID }: InvoiceWithTotal) => {
  const discountedAmount = !!discount
    ? amount - calculateRate(discount.rate, amount)
    : amount
  const taxedAmount = !!tax ? calculateRate(tax.rate, discountedAmount) : 0

  return tax === undefined ? (
    <FormattedMessage id="notApplicable" />
  ) : (
    <Amount amount={taxedAmount} unit={unitID} />
  )
}

const AmountCell = ({ tax, discount, amount, unitID }: InvoiceWithTotal) => {
  const discountedAmount = !!discount
    ? amount - calculateRate(discount.rate, amount)
    : amount
  const finalAmount = !!tax
    ? discountedAmount + calculateRate(tax.rate, discountedAmount)
    : discountedAmount

  return <Amount amount={finalAmount} unit={unitID} />
}

const statusColors = {
  PAID: 'green',
  PROCESSING: 'green',
  UNPAID: 'blue',
  LATE: 'red',
  'Pending Request': '#FBBC05',
  Financing: '#51B148',
  Published: '#1890FF',
}

const statusMessages = {
  PAID: <FormattedMessage id="paid" />,
  PROCESSING: <FormattedMessage id="processing" />,
  UNPAID: <FormattedMessage id="unpaid" />,
  LATE: <FormattedMessage id="late" />,
}

const BillStatus = (b: InvoiceWithTotal) => {
  const status =
    b.status === 'UNPAID' && new Date() > b.dueAt
      ? ('LATE' as 'LATE')
      : b.status

  return <Tag color={statusColors[status]}>{statusMessages[status]}</Tag>
}

const OrganizationDisplayName = ({ name }: { name: string }) => {
  const organization = useOrganizationByName(name)

  return (
    <Skeleton
      active={organization.isLoading}
      loading={organization.isLoading}
      title={false}
      paragraph={{ rows: 1 }}
    >
      {organization.data === 'Not Found'
        ? name
        : organization.data?.displayName}
    </Skeleton>
  )
}

export const sortOrder = <K extends string>(
  k: K,
  sort?: [K, 'asc' | 'desc']
): SortOrder =>
  !sort || sort[0] !== k ? null : sort[1] === 'asc' ? 'ascend' : 'descend'

type Col = ColumnType<InvoiceWithTotal & { key: string }>

const useDefaultColumns = (
  sort: BillSort | undefined,
  hoveredID: number | undefined
) => {
  const dispatch = useDispatch()

  return {
    id: {
      sorter: false,
      title: <FormattedMessage id="id" />,
      dataIndex: 'id',
      key: 'id',
    },
    reference: {
      sorter: true,
      sortOrder: sortOrder('reference', sort),
      title: <FormattedMessage id="reference" />,
      dataIndex: 'reference',
      key: 'reference',
    },
    discount: {
      sorter: false,
      title: <FormattedMessage id="discount" />,
      dataIndex: 'discount',
      key: 'discount',
      align: 'right',
      render: (_, t) => <DiscountCell {...t} />,
    } as Col,
    tax: {
      sorter: false,
      title: <FormattedMessage id="tax" />,
      dataIndex: 'tax',
      key: 'tax',
      align: 'right',
      render: (_, t) => <TaxCell {...t} />,
    } as Col,
    amount: {
      sorter: true,
      sortOrder: sortOrder('amount', sort),
      title: <FormattedMessage id="amount" />,
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (_, t) => <AmountCell {...t} />,
    } as Col,
    customer: {
      sorter: true,
      sortOrder: sortOrder('payer', sort),
      title: <FormattedMessage id="customer" />,
      key: 'payer',
      dataIndex: 'payer',
      render: (_, b) => {
        if (!!b.customer) {
          return b.customer.name === '' ? b.customer.email : b.customer.name
        }

        return <OrganizationDisplayName name={b.supplier} />
      },
    } as Col,
    owner: {
      sorter: true,
      title: <FormattedMessage id="organization" />,
      key: 'organization',
      dataIndex: 'organization',
      render: (_, b) => <OrganizationDisplayName name={b.organization} />,
    } as Col,
    createdAt: {
      sorter: true,
      sortOrder: sortOrder('createdAt', sort),
      dataIndex: 'createdAt',
      title: <FormattedMessage id="date" />,
      render: (_, b) => (
        <FormattedDate
          value={b.createdAt}
          year="numeric"
          month="long"
          day="numeric"
        />
      ),
    } as Col,
    dueAt: {
      sorter: true,
      sortOrder: sortOrder('dueAt', sort),
      dataIndex: 'dueAt',
      title: <FormattedMessage id="dueDate" />,
      render: (_, b) => (
        <FormattedDate
          value={b.dueAt}
          year="numeric"
          month="long"
          day="numeric"
        />
      ),
    } as Col,
    status: {
      sorter: true,
      sortOrder: sortOrder('status', sort),
      title: <FormattedMessage id="status" />,
      dataIndex: 'status',
      key: 'status',
      render: (_, b) => <BillStatus {...b} />,
    } as Col,
    paidBy: {
      sorter: false,
      title: <FormattedMessage id="paidBy" />,
      dataIndex: 'paidBy',
      key: 'paidBy',
      render: (_, b) => b.payer ?? <FormattedMessage id="notApplicable" />,
    } as Col,
    financingStatus: {
      sorter: false,
      title: <FormattedMessage id="financingStatus" />,
      dataIndex: 'financingStatus',
      key: 'financingStatus',
      render: (_, b) => (
        <FinancingStatus organization={b.organization} id={b.id} />
      ),
    } as Col,
    actions: {
      title: <FormattedMessage id="actions" />,
      key: 'action',
      align: 'right',
      render: (_, b) => (
        <ButtonGroup selected={b.id === hoveredID} align="right">
          <Button
            onClick={() => dispatch(viewBillAction(b))}
            icon={<SearchOutlined />}
          />
          <ContributorButton
            onClick={() => dispatch(editBillAction(b))}
            icon={<EditFilled />}
          />
          <ConfirmDeleteModal id={b.id} reference={b.reference} />
        </ButtonGroup>
      ),
    } as Col,
    limitedActions: {
      title: <FormattedMessage id="actions" />,
      key: 'limitedAction',
      align: 'right',
      render: (_, b) => (
        <ButtonGroup selected={b.id === hoveredID} align="right">
          <Button
            onClick={() => dispatch(viewBillAction(b))}
            icon={<SearchOutlined />}
          />
        </ButtonGroup>
      ),
    } as Col,
  }
}

export const useColumns = (
  sort: BillSort | undefined,
  hoveredID: number | undefined
): Col[] => {
  const cols = useDefaultColumns(sort, hoveredID)
  const mode = useMode()

  return mode === 'organization'
    ? [
        cols.id,
        cols.reference,
        cols.amount,
        cols.customer,
        cols.paidBy,
        cols.createdAt,
        cols.dueAt,
        cols.status,
        cols.financingStatus,
        cols.actions,
      ]
    : [
        cols.id,
        cols.reference,
        cols.amount,
        cols.customer,
        cols.owner,
        cols.createdAt,
        cols.dueAt,
        cols.financingStatus,
        cols.limitedActions,
      ]
}
